<template>
  <div class="deleted-rows">
    <button
      type="button"
      class="btn btn-outline-secondary btn-sm deleted-rows__toggle"
      :disabled="loading"
      @click="toggle"
    >
      {{ open ? label + 'を閉じる' : label + 'を表示' }}
    </button>

    <div v-if="open" class="deleted-rows__body">
      <!--
        この節で押した結果（読み込み・戻す）は、この節の頭に出す。
        表と入れ替えない（入れ替えていたので、戻せなかったときに表が消えた）。
      -->
      <ActionResultMessage :text="result.text" :type="result.type" />

      <p v-if="loading" class="text-muted mb-0">読み込んでいます…</p>

      <p v-else-if="!items.length" class="text-muted mb-0">
        {{ emptyMessage }}
      </p>

      <template v-else>
        <p class="deleted-rows__note">{{ note }}</p>
        <table class="table table-bordered mt-2 mb-0">
          <thead>
            <tr>
              <th v-for="column in columns" :key="column.key">
                {{ column.label }}
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in items" :key="row.id">
              <td v-for="column in columns" :key="column.key">
                {{ display(row, column) }}
              </td>
              <td>
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  :disabled="loading"
                  @click="restore(row)"
                >戻す</button>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ActionResultMessage from './ActionResultMessage.vue'

/**
 * 削除したものを一覧して戻す節。
 *
 * <h2>なぜ部品にするのか</h2>
 *
 * 案件・部署・取引先で同じものが要る。**3回書くと必ずどれかがずれる。**
 * この製品では「片方だけ直して、もう片方が古いまま」を何度もやっている。
 *
 * <h2>開くまで読まない</h2>
 *
 * 消したものは普段見ない。一覧を開くたびに問い合わせると、
 * 何も消していない会社にまで無駄が乗る。押したときだけ読む
 * （利用者の「無効な利用者」と同じ）。
 */
export default {
  name: 'DeletedRowsPanel',

  components: { ActionResultMessage },

  props: {
    /** 「削除した案件」など。開閉のボタンに出す。 */
    label: { type: String, required: true },

    /** 一覧を取る先。 */
    listUrl: { type: String, required: true },

    /**
     * 戻す先。{id} をIDに置き換える。
     *
     * 案件だけ形が違う（本文でIDを送る）ので、そのときは
     * restoreBody を渡す。
     */
    restoreUrl: { type: String, required: true },

    /** 本文でIDを送るなら true。 */
    restoreInBody: { type: Boolean, default: false },

    /** 出す列。{ key, label, suffix } */
    columns: { type: Array, required: true },

    /** 1件も無いときの言い方。 */
    emptyMessage: { type: String, required: true },

    /**
     * 戻したときに何が戻らないか。
     *
     * **ここを書かないと、戻したのに元どおりにならないことに
     * 気づけない。**所属や配下は人の判断が要る。
     */
    note: { type: String, required: true }
  },

  data () {
    return {
      open: false,
      loading: false,
      items: [],
      // 読み込み・戻すの結果。この節の頭に出す。
      result: { type: 'success', text: '' }
    }
  },

  methods: {
    toggle () {
      this.open = !this.open
      this.result = { type: 'success', text: '' }
      if (this.open) this.load()
    },

    display (row, column) {
      const value = row[column.key]
      if (value === null || value === undefined || value === '') return ''
      return column.suffix ? value + column.suffix : value
    },

    async load () {
      this.loading = true
      try {
        const response = await axios.get(
          process.env.VUE_APP_API_ORIGIN + this.listUrl)
        this.items = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        this.result = {
          type: 'danger',
          text: this.messageOf(error, this.label + 'を取得できませんでした。画面を再読み込みしてください。')
        }
      } finally {
        this.loading = false
      }
    },

    async restore (row) {
      const name = row.name || ''
      if (!window.confirm(name + ' を戻しますか？')) return

      this.loading = true
      this.result = { type: 'success', text: '' }
      try {
        if (this.restoreInBody) {
          await axios.post(
            process.env.VUE_APP_API_ORIGIN + this.restoreUrl, { id: row.id })
        } else {
          await axios.post(
            process.env.VUE_APP_API_ORIGIN
            + this.restoreUrl.replace('{id}', row.id))
        }
        // 戻した行はこの表から消えるので、結果は節の頭に出す。
        this.result = { type: 'success', text: '{name} を元に戻しました。一覧に表示されない場合は検索条件やページをご確認ください。'.replace('{name}', name) }
        await this.load()
        // 呼んだ側が、生きている一覧を読み直せるように知らせる。
        this.$emit('restored', row)
      } catch (error) {
        this.result = { type: 'danger', text: this.messageOf(error, '戻せませんでした。') }
      } finally {
        this.loading = false
      }
    },

    /**
     * サーバの言い分をそのまま出す。
     *
     * **「戻せませんでした」だけでは何を直せばよいか分からない。**
     * 取引先ごと消えている、といった理由はサーバが持っている。
     */
    messageOf (error, fallback) {
      const body = error && error.response && error.response.data
      if (typeof body === 'string' && body.trim()) return body
      if (body && typeof body.message === 'string' && body.message.trim()) {
        return body.message
      }
      return fallback
    }
  }
}
</script>

<style scoped>
.deleted-rows { margin-top: 16px; }
.deleted-rows__body { margin-top: 10px; }
.deleted-rows__note { color: #5c6873; font-size: 13px; margin-bottom: 0; }
.deleted-rows__error { color: #e55353; margin-bottom: 0; }
</style>
